import React from "react";
import { Box, Tooltip, useMediaQuery } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { dt } from "../../../shared/themes/redesignTokens";
import { LifecycleStage } from "../utils/lifecycleStages";
import DeadlineBar from "./DeadlineBar";
import { formatUiDate } from "../../../shared/utils/dateFormat";

// ════════════════════════════════════════════════════════════════
//  Styles — Inject stepperPulse keyframe alongside redesign setup
// ════════════════════════════════════════════════════════════════

const STEPPER_STYLE_ID = "lifecycle-stepper-keyframes";

function ensureStepperKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STEPPER_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STEPPER_STYLE_ID;
  style.textContent = `
    @keyframes stepperPulse {
      0%, 100% { box-shadow: 0 0 0 4px rgba(52,152,219,0.12); }
      50% { box-shadow: 0 0 0 7px rgba(52,152,219,0.18); }
    }
  `;
  document.head.appendChild(style);
}

// ════════════════════════════════════════════════════════════════
//  Color tokens
// ════════════════════════════════════════════════════════════════

const COLORS = {
  completedBg: "#27AE60",
  completedIcon: "#FFFFFF",
  currentGradientFrom: dt.blue,
  currentGradientTo: dt.gradientDark,
  currentIcon: "#FFFFFF",
  currentGlow: "rgba(52,152,219,0.15)",
  upcomingBorder: "#D1D5DB",
  upcomingBg: "#F4F6F9",
  upcomingIcon: "#9CA3AF",
  failedBg: "#C0392B",
  failedIcon: "#FFFFFF",
  lineCompleted: "#27AE60",
  lineFuture: "#D1D5DB",
  labelCompleted: dt.textPrimary,
  labelCurrent: dt.textPrimary,
  labelUpcoming: dt.textMuted,
  labelFailed: "#C0392B",
  // Compact dots
  dotCompleted: "#27AE60",
  dotCurrent: dt.blue,
  dotUpcoming: "#D1D5DB",
  dotFailed: "#C0392B",
};

// ════════════════════════════════════════════════════════════════
//  Props
// ════════════════════════════════════════════════════════════════

interface ServiceLifecycleTrackerProps {
  stages: LifecycleStage[];
  variant: "full" | "compact" | "mini" | "sidebar" | "expanded-sidebar" | "expanded-drawer" | "card-horizontal";
}

// ════════════════════════════════════════════════════════════════
//  Main Component
// ════════════════════════════════════════════════════════════════

const ServiceLifecycleTracker: React.FC<ServiceLifecycleTrackerProps> = ({ stages, variant }) => {
  useRedesignSetup();

  React.useEffect(() => {
    ensureStepperKeyframes();
  }, []);

  if (variant === "mini") return <MiniVariant stages={stages} />;
  if (variant === "compact") return <CompactVariant stages={stages} />;
  if (variant === "card-horizontal") return <CardHorizontalVariant stages={stages} />;
  if (variant === "expanded-drawer") return <ExpandedDrawerVariant stages={stages} />;
  if (variant === "expanded-sidebar") return <ExpandedSidebarVariant stages={stages} />;
  if (variant === "sidebar") return <SidebarVariant stages={stages} />;
  return <FullVariant stages={stages} />;
};

// ════════════════════════════════════════════════════════════════
//  FULL Variant
// ════════════════════════════════════════════════════════════════

const NODE_SIZE = 34;
const ICON_SIZE = 18;

const FullVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  const currentIndex = stages.findIndex((s) => s.status === "current");
  const currentStep = currentIndex >= 0 ? currentIndex + 1 : stages.length;
  const total = stages.length;

  if (isPhone) {
    return (
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: "14px",
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          padding: "16px 20px",
          mb: 1.5,
        }}
      >
        {/* Step counter */}
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 600,
            color: dt.textSecondary,
            letterSpacing: "0.3px",
            mb: 1.5,
          }}
        >
          {translate("servicing:lifecycle.stepOf", { current: currentStep, total })}
        </Box>

        {/* Vertical stepper */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {stages.map((stage, idx) => {
            const isLast = idx === stages.length - 1;
            return (
              <Box key={stage.step} sx={{ display: "flex", alignItems: "stretch" }}>
                {/* Node column */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mr: 1.5,
                  }}
                >
                  <StepNode stage={stage} size={28} iconSize={14} />
                  {!isLast && (
                    <Box
                      sx={{
                        width: "2px",
                        flex: 1,
                        minHeight: "16px",
                        backgroundColor:
                          stage.status === "completed"
                            ? COLORS.lineCompleted
                            : COLORS.lineFuture,
                        borderStyle:
                          stage.status === "completed" || stage.status === "current"
                            ? "solid"
                            : "none",
                        ...(stage.status !== "completed" &&
                          stage.status !== "current" && {
                            background: `repeating-linear-gradient(to bottom, ${COLORS.lineFuture} 0px, ${COLORS.lineFuture} 3px, transparent 3px, transparent 6px)`,
                            backgroundColor: "transparent",
                          }),
                      }}
                    />
                  )}
                </Box>
                {/* Label */}
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: stage.status === "current" ? 600 : stage.status === "completed" ? 500 : 400,
                    color:
                      stage.status === "failed"
                        ? COLORS.labelFailed
                        : stage.status === "upcoming"
                        ? COLORS.labelUpcoming
                        : COLORS.labelCurrent,
                    py: "4px",
                    lineHeight: "28px",
                  }}
                >
                  {translate(stage.labelKey)}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Desktop: horizontal stepper
  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        borderRadius: "14px",
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        padding: "16px 24px",
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {stages.map((stage, idx) => {
          const isLast = idx === stages.length - 1;

          // Determine line color between this node and the next
          const nextStage = !isLast ? stages[idx + 1] : null;
          const lineColor = stage.status === "completed" ? COLORS.lineCompleted : COLORS.lineFuture;
          const lineStyle =
            stage.status === "completed"
              ? "solid"
              : "dashed";

          // Gradient line when transitioning from current to upcoming
          const isGradientLine = stage.status === "current";

          return (
            <React.Fragment key={stage.step}>
              {/* Node + label group */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <StepNode stage={stage} size={NODE_SIZE} iconSize={ICON_SIZE} />
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight:
                      stage.status === "completed" || stage.status === "current"
                        ? 600
                        : 400,
                    color:
                      stage.status === "failed"
                        ? COLORS.labelFailed
                        : stage.status === "upcoming"
                        ? COLORS.labelUpcoming
                        : COLORS.labelCurrent,
                    mt: "6px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    maxWidth: "80px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textDecoration: stage.status === "failed" ? "line-through" : "none",
                  }}
                >
                  {translate(stage.labelKey)}
                </Box>
              </Box>

              {/* Connecting line */}
              {!isLast && (
                <Box
                  sx={{
                    flex: 1,
                    height: "2px",
                    mx: "6px",
                    mt: "-18px",
                    ...(isGradientLine
                      ? {
                          background: `linear-gradient(to right, ${COLORS.currentGradientFrom}, ${COLORS.lineFuture})`,
                        }
                      : {
                          backgroundColor: lineColor,
                          ...(lineStyle === "dashed" && {
                            background: `repeating-linear-gradient(to right, ${COLORS.lineFuture} 0px, ${COLORS.lineFuture} 5px, transparent 5px, transparent 10px)`,
                            backgroundColor: "transparent",
                          }),
                        }),
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════
//  Step Node — shared circle with icon
// ════════════════════════════════════════════════════════════════

interface StepNodeProps {
  stage: LifecycleStage;
  size: number;
  iconSize: number;
}

const StepNode: React.FC<StepNodeProps> = ({ stage, size, iconSize }) => {
  const Icon = stage.icon;

  let bgStyle: any = {};
  let iconColor = COLORS.upcomingIcon;
  let borderStyle: string | undefined;
  let animationStyle: any = {};

  switch (stage.status) {
    case "completed":
      bgStyle = { backgroundColor: COLORS.completedBg };
      iconColor = COLORS.completedIcon;
      break;
    case "current":
      bgStyle = {
        background: `linear-gradient(135deg, ${COLORS.currentGradientFrom}, ${COLORS.currentGradientTo})`,
      };
      iconColor = COLORS.currentIcon;
      animationStyle = {
        animation: "stepperPulse 2s ease-in-out infinite",
      };
      break;
    case "failed":
      bgStyle = { backgroundColor: COLORS.failedBg };
      iconColor = COLORS.failedIcon;
      break;
    case "upcoming":
    default:
      bgStyle = { backgroundColor: COLORS.upcomingBg };
      borderStyle = `2px solid ${COLORS.upcomingBorder}`;
      iconColor = COLORS.upcomingIcon;
      break;
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        border: borderStyle,
        ...bgStyle,
        ...animationStyle,
        transition: "all 0.3s ease",
      }}
    >
      {stage.status === "completed" ? (
        <CheckIcon sx={{ fontSize: iconSize, color: iconColor }} />
      ) : stage.status === "failed" ? (
        <CloseIcon sx={{ fontSize: iconSize, color: iconColor }} />
      ) : (
        <Icon sx={{ fontSize: iconSize, color: iconColor }} />
      )}
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════
//  SIDEBAR Variant — vertical stepper for card right zone
// ════════════════════════════════════════════════════════════════

const SIDEBAR_NODE = 20;
const SIDEBAR_ICON = 11;
const SIDEBAR_LINE_HEIGHT = 14;

const SidebarVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        return (
          <Box key={stage.step} sx={{ display: "flex", alignItems: "stretch" }}>
            {/* Node column */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mr: 1,
              }}
            >
              <StepNode stage={stage} size={SIDEBAR_NODE} iconSize={SIDEBAR_ICON} />
              {!isLast && (
                <Box
                  sx={{
                    width: "2px",
                    height: `${SIDEBAR_LINE_HEIGHT}px`,
                    flexShrink: 0,
                    backgroundColor:
                      stage.status === "completed"
                        ? COLORS.lineCompleted
                        : "transparent",
                    ...(stage.status !== "completed" && {
                      background: `repeating-linear-gradient(to bottom, ${
                        stage.status === "current" ? COLORS.currentGradientFrom : COLORS.lineFuture
                      } 0px, ${
                        stage.status === "current" ? COLORS.currentGradientFrom : COLORS.lineFuture
                      } 3px, transparent 3px, transparent 6px)`,
                    }),
                  }}
                />
              )}
            </Box>
            {/* Label */}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight:
                  stage.status === "current" ? 600 : stage.status === "completed" ? 500 : 400,
                color:
                  stage.status === "failed"
                    ? COLORS.labelFailed
                    : stage.status === "upcoming"
                    ? COLORS.labelUpcoming
                    : COLORS.labelCurrent,
                lineHeight: `${SIDEBAR_NODE}px`,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: stage.status === "failed" ? "line-through" : "none",
              }}
            >
              {translate(stage.labelKey)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════
//  COMPACT Variant — small dots with label
// ════════════════════════════════════════════════════════════════

const CompactVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);

  const currentIndex = stages.findIndex((s) => s.status === "current");
  const currentStep = currentIndex >= 0 ? currentIndex + 1 : stages.length;
  const total = stages.length;

  // Status label for tooltip
  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed": return translate("serviceOffers:lifecycle.tooltipStatus.completed");
      case "current": return translate("serviceOffers:lifecycle.tooltipStatus.current");
      case "upcoming": return translate("serviceOffers:lifecycle.tooltipStatus.upcoming");
      case "failed": return translate("serviceOffers:lifecycle.tooltipStatus.failed");
      default: return "";
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
        {stages.map((stage) => {
          const isCurrent = stage.status === "current";
          const dotSize = isCurrent ? 8 : 6;

          let bg: string;
          switch (stage.status) {
            case "completed":
              bg = COLORS.dotCompleted;
              break;
            case "current":
              bg = COLORS.dotCurrent;
              break;
            case "failed":
              bg = COLORS.dotFailed;
              break;
            default:
              bg = COLORS.dotUpcoming;
          }

          const tooltipText = `${translate(stage.labelKey)} \u2014 ${getStatusLabel(stage.status)}`;

          return (
            <Tooltip key={stage.step} title={tooltipText} arrow enterDelay={300} placement="top">
              <Box
                sx={{
                  width: dotSize,
                  height: dotSize,
                  borderRadius: "50%",
                  backgroundColor: bg,
                  flexShrink: 0,
                  transition: "all 0.2s ease",
                  ...(isCurrent && {
                    boxShadow: `0 0 0 3px rgba(52,152,219,0.18)`,
                  }),
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Tooltip
        title={translate("serviceOffers:lifecycle.stepOf", {
          current: currentStep,
          total,
          stageName: currentIndex >= 0 ? translate(stages[currentIndex].labelKey) : translate(stages[stages.length - 1].labelKey),
        })}
        arrow
        enterDelay={300}
        placement="top"
      >
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 500,
            color: dt.textMuted,
            letterSpacing: "0.2px",
            whiteSpace: "nowrap",
          }}
        >
          {currentStep}/{total}
        </Box>
      </Tooltip>
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════
//  MINI Variant — dots only, no label
// ════════════════════════════════════════════════════════════════

const MiniVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);

  const currentIndex = stages.findIndex((s) => s.status === "current");
  const currentStep = currentIndex >= 0 ? currentIndex + 1 : stages.length;
  const total = stages.length;
  const currentStageName = currentIndex >= 0
    ? translate(stages[currentIndex].labelKey)
    : translate(stages[stages.length - 1].labelKey);

  const miniTooltip = translate("serviceOffers:lifecycle.stepOf", {
    current: currentStep,
    total,
    stageName: currentStageName,
  });

  return (
    <Tooltip title={miniTooltip} arrow enterDelay={300} placement="top">
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
        {stages.map((stage) => {
          const isCurrent = stage.status === "current";
          const dotSize = isCurrent ? 6 : 5;

          let bg: string;
          switch (stage.status) {
            case "completed":
              bg = COLORS.dotCompleted;
              break;
            case "current":
              bg = COLORS.dotCurrent;
              break;
            case "failed":
              bg = COLORS.dotFailed;
              break;
            default:
              bg = COLORS.dotUpcoming;
          }

          return (
            <Box
              key={stage.step}
              sx={{
                width: dotSize,
                height: dotSize,
                borderRadius: "50%",
                backgroundColor: bg,
                flexShrink: 0,
                transition: "all 0.2s ease",
                ...(isCurrent && {
                  boxShadow: `0 0 0 2px rgba(52,152,219,0.18)`,
                }),
              }}
            />
          );
        })}
      </Box>
    </Tooltip>
  );
};

// ════════════════════════════════════════════════════════════════
//  EXPANDED SIDEBAR Variant — enriched vertical stepper with
//  description card, action prompt, and deadline bar
// ════════════════════════════════════════════════════════════════

const EXPANDED_NODE = 24;
const EXPANDED_ICON = 13;
const EXPANDED_LINE_HEIGHT = 18;

const ExpandedSidebarVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        const isCurrent = stage.status === "current";
        const isFailed = stage.status === "failed";
        const showExpansionCard = isCurrent || isFailed;

        // Determine expansion card colors
        const cardBg = isFailed ? "rgba(239,68,68,0.04)" : dt.blueSoft;
        const cardBorder = isFailed
          ? "1px solid rgba(239,68,68,0.15)"
          : "1px solid rgba(52,152,219,0.15)";

        return (
          <Box key={stage.step} sx={{ display: "flex", alignItems: "stretch" }}>
            {/* Node column */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mr: 1,
                flexShrink: 0,
              }}
            >
              <StepNode stage={stage} size={EXPANDED_NODE} iconSize={EXPANDED_ICON} />
              {!isLast && (
                <Box
                  sx={{
                    width: "2px",
                    height: showExpansionCard ? "auto" : `${EXPANDED_LINE_HEIGHT}px`,
                    flex: showExpansionCard ? 1 : "none",
                    minHeight: showExpansionCard ? `${EXPANDED_LINE_HEIGHT}px` : undefined,
                    flexShrink: 0,
                    backgroundColor:
                      stage.status === "completed"
                        ? COLORS.lineCompleted
                        : "transparent",
                    ...(stage.status !== "completed" && {
                      background: `repeating-linear-gradient(to bottom, ${
                        isCurrent ? COLORS.currentGradientFrom : COLORS.lineFuture
                      } 0px, ${
                        isCurrent ? COLORS.currentGradientFrom : COLORS.lineFuture
                      } 3px, transparent 3px, transparent 6px)`,
                    }),
                  }}
                />
              )}
            </Box>

            {/* Label + expansion content */}
            <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : "2px" }}>
              {/* Stage label row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight:
                      isCurrent ? 600 : stage.status === "completed" ? 500 : 400,
                    color:
                      isFailed
                        ? COLORS.labelFailed
                        : stage.status === "upcoming"
                        ? COLORS.labelUpcoming
                        : COLORS.labelCurrent,
                    lineHeight: `${EXPANDED_NODE}px`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textDecoration: isFailed ? "line-through" : "none",
                  }}
                >
                  {translate(stage.labelKey)}
                </Box>
              </Box>

              {/* Timestamp for completed stages */}
              {stage.status === "completed" && stage.timestamp && (
                <Box
                  sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "10px",
                    color: dt.textMuted,
                    lineHeight: 1.2,
                    mt: "-2px",
                    mb: "2px",
                  }}
                >
                  {formatUiDate(stage.timestamp, "dayMonthTime")}
                </Box>
              )}

              {/* Expansion card for current / failed stages */}
              {showExpansionCard && (stage.descriptionKey || stage.actionPromptKey || stage.deadlineInfo) && (
                <Box
                  sx={{
                    backgroundColor: cardBg,
                    border: cardBorder,
                    borderRadius: "8px",
                    padding: "10px 12px",
                    mt: "6px",
                    mb: "4px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  {/* Description text */}
                  {stage.descriptionKey && (
                    <Box
                      sx={{
                        fontSize: "12px",
                        fontFamily: dt.font,
                        color: dt.textSecondary,
                        lineHeight: 1.4,
                      }}
                    >
                      {translate(stage.descriptionKey)}
                    </Box>
                  )}

                  {/* Action prompt with blue accent bar */}
                  {stage.actionPromptKey && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        gap: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          width: "3px",
                          borderRadius: "2px",
                          backgroundColor: isFailed ? "#EF4444" : dt.blue,
                          flexShrink: 0,
                        }}
                      />
                      <Box
                        sx={{
                          fontSize: "12px",
                          fontFamily: dt.font,
                          fontWeight: 600,
                          color: isFailed ? "#EF4444" : dt.textPrimary,
                          lineHeight: 1.4,
                        }}
                      >
                        {translate(stage.actionPromptKey)}
                      </Box>
                    </Box>
                  )}

                  {/* Deadline bar */}
                  {stage.deadlineInfo && (
                    <DeadlineBar
                      deadline={stage.deadlineInfo.deadline}
                      startDate={stage.deadlineInfo.startDate}
                      compact
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════
//  EXPANDED DRAWER Variant — hybrid stepper with inline expansion
//  card at the current/failed stage. 300px right zone.
// ════════════════════════════════════════════════════════════════

const DW = { node: 22, icon: 12, line: 16, col: 30 };

const drawerLineSx = (status: string) => {
  if (status === "completed") return { width: "2px", backgroundColor: COLORS.lineCompleted };
  const c = status === "current" ? COLORS.currentGradientFrom : COLORS.lineFuture;
  return { width: "2px", background: `repeating-linear-gradient(to bottom, ${c} 0px, ${c} 3px, transparent 3px, transparent 6px)`, backgroundColor: "transparent" as const };
};

const ExpandedDrawerVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        const isCur = stage.status === "current";
        const isFail = stage.status === "failed";
        const isDone = stage.status === "completed";
        const expand = (isCur || isFail) && (stage.descriptionKey || stage.actionPromptKey || stage.otherSideContextKey || stage.deadlineInfo);
        const eBg = isFail ? "rgba(239,68,68,0.04)" : dt.blueSoft;
        const eBdr = isFail ? "1px solid rgba(239,68,68,0.15)" : "1px solid rgba(52,152,219,0.15)";

        return (
          <Box key={stage.step}>
            {/* Node + label row */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${DW.col}px`, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                <StepNode stage={stage} size={DW.node} iconSize={DW.icon} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, ml: "8px" }}>
                <Box sx={{
                  fontFamily: dt.font, fontSize: "11px",
                  fontWeight: isCur ? 600 : isDone ? 500 : 400,
                  color: isFail ? COLORS.labelFailed : stage.status === "upcoming" ? COLORS.labelUpcoming : COLORS.labelCurrent,
                  lineHeight: `${DW.node}px`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  textDecoration: isFail ? "line-through" : "none",
                }}>
                  {translate(stage.labelKey)}
                </Box>
                {isDone && stage.timestamp && (
                  <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", color: dt.textMuted, lineHeight: 1.2, mt: "-2px" }}>
                    {formatUiDate(stage.timestamp, "dayMonthTime")}
                  </Box>
                )}
              </Box>
            </Box>
            {/* Expansion card for current/failed */}
            {expand && (
              <Box sx={{ backgroundColor: eBg, border: eBdr, borderRadius: "8px", padding: "10px 12px", mt: "6px", mb: "6px", ml: `${DW.col}px`, display: "flex", flexDirection: "column" }}>
                {stage.descriptionKey && (
                  <Box sx={{ fontSize: "12px", fontFamily: dt.font, color: dt.textSecondary, lineHeight: 1.4 }}>
                    {translate(stage.descriptionKey)}
                  </Box>
                )}
                {stage.actionPromptKey && (
                  <Box sx={{ borderLeft: `3px solid ${isFail ? "#EF4444" : dt.blue}`, pl: "10px", fontSize: "12px", fontWeight: 600, fontFamily: dt.font, color: isFail ? "#EF4444" : dt.textPrimary, lineHeight: 1.4, mt: "6px" }}>
                    {translate(stage.actionPromptKey)}
                  </Box>
                )}
                {stage.otherSideContextKey && (
                  <Box sx={{ fontSize: "11px", fontFamily: dt.font, color: dt.textMuted, fontStyle: "italic", lineHeight: 1.4, mt: "4px" }}>
                    {translate(stage.otherSideContextKey)}
                  </Box>
                )}
                {stage.deadlineInfo && (
                  <Box sx={{ mt: "8px" }}>
                    <DeadlineBar deadline={stage.deadlineInfo.deadline} startDate={stage.deadlineInfo.startDate} compact />
                  </Box>
                )}
              </Box>
            )}
            {/* Connecting line */}
            {!isLast && (
              <Box sx={{ display: "flex", justifyContent: "center", width: `${DW.col}px` }}>
                <Box sx={{ height: `${DW.line}px`, flexShrink: 0, ...drawerLineSx(stage.status) }} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ════════════════════════════════════════════════════════════════
//  CARD HORIZONTAL Variant — inline horizontal stepper for card body
//  20px nodes, 2px lines, 10px labels below. On phone: falls back to
//  compact dots so it doesn't overflow.
// ════════════════════════════════════════════════════════════════

const CH = { node: 20, icon: 11, labelSize: "10px" as const };

const CardHorizontalVariant: React.FC<{ stages: LifecycleStage[] }> = ({ stages }) => {
  const { translate } = useTranslation(["servicing", "serviceOffers"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  // On phone, delegate to compact dots to avoid overflow
  if (isPhone) return <CompactVariant stages={stages} />;

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      {stages.map((stage, idx) => {
        const isLast = idx === stages.length - 1;

        const lineColor = stage.status === "completed" ? COLORS.lineCompleted : COLORS.lineFuture;
        const isGradientLine = stage.status === "current";

        return (
          <React.Fragment key={stage.step}>
            {/* Node + label */}
            <Tooltip
              title={translate(stage.labelKey)}
              arrow
              enterDelay={300}
              placement="top"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <StepNode stage={stage} size={CH.node} iconSize={CH.icon} />
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: CH.labelSize,
                    fontWeight:
                      stage.status === "completed" || stage.status === "current"
                        ? 600
                        : 400,
                    color:
                      stage.status === "failed"
                        ? COLORS.labelFailed
                        : stage.status === "upcoming"
                        ? COLORS.labelUpcoming
                        : COLORS.labelCurrent,
                    mt: "3px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    maxWidth: "64px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textDecoration: stage.status === "failed" ? "line-through" : "none",
                  }}
                >
                  {translate(stage.labelKey)}
                </Box>
              </Box>
            </Tooltip>

            {/* Connecting line */}
            {!isLast && (
              <Box
                sx={{
                  flex: 1,
                  height: "2px",
                  mx: "4px",
                  mt: "-14px",
                  ...(isGradientLine
                    ? {
                        background: `linear-gradient(to right, ${COLORS.currentGradientFrom}, ${COLORS.lineFuture})`,
                      }
                    : {
                        backgroundColor: lineColor,
                        ...(stage.status !== "completed" && {
                          background: `repeating-linear-gradient(to right, ${COLORS.lineFuture} 0px, ${COLORS.lineFuture} 4px, transparent 4px, transparent 8px)`,
                          backgroundColor: "transparent",
                        }),
                      }),
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default ServiceLifecycleTracker;
